<template>
  <div class="dynamic-cohort-builder">
    <div class="builder-header">
      <p class="txtHeaderStyle">{{ $t('members.Configure_Dynamic_Rules') }}</p>
      <div class="opa-05 fc-000000 fs-14 mt-8">
        {{ $t('members.Dynamic_cohort_description') }}
      </div>
    </div>

    <div class="builder-content">
      <rule-group
        v-if="rootGroup"
        :group="rootGroup"
        :can-remove="false"
        :is-root="true"
        :available-attributes="availableAttributes"
        :blur-mode="blurMode"
        @update="onGroupUpdate"
      ></rule-group>
    </div>

    <div class="builder-footer">
      <el-button
        type="primary"
        class="btn-preview"
        :loading="previewLoading"
        @click="previewCohort"
      >
        {{ $t('members.Preview') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import RuleGroup from './RuleGroup.vue';
import { getAllAttributes } from './attribute-config';
import { evaluateOperator, getMemberFieldValue } from './rule-evaluator';
import _ from 'lodash';

export default {
  name: 'DynamicCohortBuilder',
  components: {
    RuleGroup,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    blurMode: {
      type: Boolean,
      default: false,
    },
    modalForm: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      rootGroup: null,
      previewLoading: false,
      debouncedEmit: null,
    };
  },
  computed: {
    availableAttributes() {
      return getAllAttributes();
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (newValue) {
          this.rootGroup = { ...newValue };
        } else {
          this.initializeRootGroup();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    // Debounce emit to avoid too many updates
    this.debouncedEmit = _.debounce(() => {
      this.$emit('input', this.rootGroup);
      this.$emit('change', this.rootGroup);
    }, 400);
  },
  methods: {
    initializeRootGroup() {
      this.rootGroup = {
        id: 'root',
        op: 'AND',
        children: [
          {
            id: this.generateId(),
            field: '',
            operator: '',
            value: undefined,
          },
        ],
      };
    },
    onGroupUpdate(updatedGroup) {
      this.rootGroup = updatedGroup;
      this.debouncedEmit();
      this.$emit('rules-updated', updatedGroup);
    },
    setPreviewLoading(loading) {
      this.previewLoading = loading;
      this.$emit('preview-loading', loading);
    },
    setPreviewUpdatedByEmails(emails) {
      const membersall = this.$store.getters['membersCohort/membersall'];
      const matchingMembers = _.values(membersall).filter((member) => emails.includes(member.email));
      const clientPreview =  {
        count: matchingMembers.length,
        members: matchingMembers.slice(0, 50), // Limit to 50 for preview
      };
      this.$emit('preview-updated', clientPreview);
    },
    async initializeMembers() {
      this.setPreviewLoading(true);
      await this.$store.dispatch("membersCohort/getDynamicCohortMembers", { id: this.modalForm.id });
      const dynamicCohortMembers = this.$store.getters['membersCohort/dynamicCohortMembers'];
      this.setPreviewUpdatedByEmails(dynamicCohortMembers);
      this.setPreviewLoading(false);
    },
    async previewCohort() {
      if (!this.rootGroup || !this.hasValidRules()) {
        this.$message({
          type: 'warning',
          message: this.$t('members.Please_add_at_least_one_valid_rule'),
        });
        return;
      }

      this.previewLoading = true;
      this.$emit('preview-loading', true);
      
      try {
        this.setPreviewLoading(true);
        await this.$store.dispatch('membersCohort/previewDynamicCohort', this.modalForm);
        const dynamicCohortMembers = this.$store.getters['membersCohort/dynamicCohortMembers'];
        this.setPreviewUpdatedByEmails(dynamicCohortMembers);
        this.setPreviewLoading(false);
      } catch (error) {
        console.error('Error previewing cohort:', error);
        this.$message({
          type: 'error',
          message: this.$t('members.Error_previewing_cohort'),
        });
      } finally {
        this.previewLoading = false;
        this.$emit('preview-loading', false);
      }
    },
    hasValidRules() {
      if (!this.rootGroup || !this.rootGroup.children || this.rootGroup.children.length === 0) {
        return false;
      }
      // Check if at least one rule has field, operator, and value
      const hasValidRule = (children) => {
        return children.some((child) => {
          if (child.op) {
            // It's a group, recurse
            return hasValidRule(child.children || []);
          } else {
            // It's a rule
            return child.field && child.operator && this.hasValidValue(child);
          }
        });
      };
      return hasValidRule(this.rootGroup.children);
    },
    hasValidValue(rule) {
      if (rule.operator === 'BETWEEN') {
        return Array.isArray(rule.value) && rule.value[0] != null && rule.value[1] != null;
      } else if (['IN', 'NOT_IN'].includes(rule.operator)) {
        return Array.isArray(rule.value) && rule.value.length > 0;
      } else {
        return rule.value != null && rule.value !== '';
      }
    },
    canDoClientSidePreview() {
      // Only do client-side preview for simple Team/House filters if data is available
      const membersall = this.$store.getters['membersCohort/membersall'];
      return membersall && Object.keys(membersall).length > 0;
    },
    performClientSidePreview() {
      // Simple client-side filtering for Team/House rules only
      const membersall = this.$store.getters['membersCohort/membersall'];
      const membersByTeam = this.$store.getters['membersCohort/membersByTeam'];
      const membersByHouse = this.$store.getters['membersCohort/membersByHouse'];

      const evaluateRule = (rule, member) => {
        if (!rule.field || !rule.operator) {
          return false;
        }

        // Get member field value using the reusable utility function
        // This handles field mapping and nested field access automatically
        const memberValue = getMemberFieldValue(member, rule.field);

        // Use the reusable operator evaluator
        return evaluateOperator(rule.operator, memberValue, rule.value);
      };

      const evaluateGroup = (group, member) => {
        if (!group.children || group.children.length === 0) return false;

        const results = group.children.map((child) => {
          if (child.op) {
            return evaluateGroup(child, member);
          } else {
            return evaluateRule(child, member);
          }
        });

        return group.op === 'AND' ? results.every((r) => r) : results.some((r) => r);
      };

      const matchingMembers = [];
      for (const email in membersall) {
        const member = membersall[email];
        if (evaluateGroup(this.rootGroup, member)) {
          matchingMembers.push(member);
        }
      }

      return {
        count: matchingMembers.length,
        members: matchingMembers.slice(0, 50), // Limit to 50 for preview
      };
    },
    generateId() {
      return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-cohort-builder {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;

  .builder-header {
    margin-bottom: 10px;
    flex-shrink: 0;
    
    .txtHeaderStyle {
      font-size: 17px;
      font-weight: 900;
      color: #000000;
      margin: 0 0 4px 0;
    }
    
    .opa-05 {
      opacity: 0.5;
      font-size: 12px;
      line-height: 1.4;
    }
  }

  .builder-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    background-color: #ffffff;
    border: 1px solid #e4e4e4;
    border-radius: 8px;
    margin-bottom: 8px;
    
    // Thin scrollbar styling to match existing patterns
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #c0c0c0;
      border-radius: 4px;
      
      &:hover {
        background: #a0a0a0;
      }
    }
  }

  .builder-footer {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;

    .btnAddTeamModal {
      background-color: #32c2d5;
      border-color: #32c2d5;
      color: #ffffff;
      height: 44px;
      padding: 0 32px;
      font-size: 15px;
      font-weight: 700;
      border-radius: 4px;
      transition: all 0.2s ease;

      &:hover {
        background-color: #2ab3c4;
        border-color: #2ab3c4;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(50, 194, 213, 0.3);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}
</style>

